import { collection, doc, documentId, getDoc, getDocs, query, where, type DocumentData } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import {
  ageInMonths,
  assessNamat,
  readIndicators,
  type ExamInput,
  type ExamValues,
  type IndicatorReading,
  type NamatAssessment,
  type PersonInput,
  type ResponseInput,
} from '@/lib/assessment/namatAssessment';
import { buildScaleBreakdown, type ScaleBreakdown } from '@/lib/assessment/scaleBreakdown';
import { isPreviousMonth } from '@/lib/assessment/questionnaireResponseKey';
import { groupByPillar, maxOptionScore, pillarScores, type Pillar, type PillarScore } from '@/lib/assessment/pillarScores';
import { toBirthDate } from '@/lib/patient/age';
import { CHUNK_CONCURRENCY, IN_CHUNK, chunkIds, forEachLimited, groupBy } from './batchedIn';

// Reads what the Namat scale needs and hands it to the pure assessNamat
// (spec #86). Values come back from Firestore as numbers or strings.
//
// Two read paths feed one pure summarize(): loadClinicalSummary reads one
// beneficiary (3 queries); loadClinicalSummaries reads a whole roster with
// `in` queries of 30 ids (3 queries per 30 beneficiaries, #73).

const toDate = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const t = v as { toDate?: () => Date };
  if (typeof t.toDate === 'function') return t.toDate();
  const d = new Date(v as string);
  return Number.isNaN(d.getTime()) ? null : d;
};

type Bilingual = { en: string; ar: string };

/** A questionnaire question as stored, the parts the scale screens read. */
export interface FactQuestion {
  id: string;
  category?: string;
  question?: Bilingual;
  options?: { id?: string; text?: Bilingual; score?: unknown }[];
}

export interface QuestionnaireFacts {
  maxScoreByQuestion?: Record<string, number>;
  /** The questionnaire's own validity (#92); undefined = the default. */
  validityDays?: number;
  /** Its questions (#124): pillar and texts for answers saved without them. */
  questions?: FactQuestion[];
}

const factsCache = new Map<string, Promise<QuestionnaireFacts>>();

function questionnaireFacts(questionnaireId: string): Promise<QuestionnaireFacts> {
  if (!factsCache.has(questionnaireId)) {
    factsCache.set(
      questionnaireId,
      getDoc(doc(db, 'questionnaires', questionnaireId))
        .then((snap) => {
          const questions = (snap.data()?.questions || []) as FactQuestion[];
          const validityDays = (snap.data() as { validityDays?: unknown } | undefined)?.validityDays;
          return {
            maxScoreByQuestion: Object.fromEntries(questions.map((q) => [q.id, maxOptionScore(q.options)])),
            validityDays: typeof validityDays === 'number' && validityDays > 0 ? validityDays : undefined,
            questions,
          };
        })
        .catch((): QuestionnaireFacts => ({}))
    );
  }
  return factsCache.get(questionnaireId)!;
}

/** One user's dated questionersanswers rows, newest first (quarter- and month-keyed alike). */
function datedRows(rows: DocumentData[]): { r: DocumentData; at: Date }[] {
  return rows
    .map((r) => ({ r, at: toDate(r.completedAt) }))
    .filter((x): x is { r: DocumentData; at: Date } => x.at !== null)
    .sort((a, b) => b.at.getTime() - a.at.getTime());
}

/** The newest dated row among one user's questionersanswers rows. */
function latestRow(rows: DocumentData[]): { r: DocumentData; at: Date } | null {
  return datedRows(rows)[0] ?? null;
}

/** The newest dated response among one user's questionersanswers rows. */
function latestResponse(rows: DocumentData[]): { response: ResponseInput; questionnaireId?: string } | null {
  const top = latestRow(rows);
  if (!top) return null;
  const answers = ((top.r.answers || []) as { questionId?: string; score?: unknown }[])
    .filter((a) => a.questionId)
    .map((a) => ({ questionId: a.questionId as string, score: Number(a.score) || 0 }));
  return { response: { completedAt: top.at, answers }, questionnaireId: top.r.questionnaireId };
}

function toExams(rows: DocumentData[]): ExamInput[] {
  return rows
    .map((e) => ({ ...e, date: toDate(e.date) || toDate(e.createdAt) }))
    .filter((e): e is ExamInput => e.date !== null);
}

/** Birth date and sex from a users/{uid} document's data; null = the read failed. */
function toPerson(userData: DocumentData | null): PersonInput {
  if (userData === null) return {};
  const sex = userData.gender === 'male' || userData.gender === 'female' ? userData.gender : null;
  return { birthDate: toBirthDate(userData.dateOfBirth), sex };
}

/** One beneficiary's raw rows, as read from Firestore. */
export interface ClinicalRows {
  /** questionersanswers rows (userId == uid). */
  answers: DocumentData[];
  /** clinicalExams rows (customerId == uid). */
  exams: DocumentData[];
  /** users/{uid} data ({} when the doc is missing), or null when the read failed. */
  user: DocumentData | null;
}

export interface ClinicalSummary {
  assessment: NamatAssessment;
  /** The latest completed exam's health indicators with their levels (#91), age at that exam. */
  indicators: IndicatorReading[];
}

/**
 * Pure: one beneficiary's rows and the facts of the questionnaire their latest
 * response answered → their scale and the latest exam's indicators.
 */
export function summarize(rows: ClinicalRows, facts: QuestionnaireFacts, now: Date): ClinicalSummary {
  const latest = latestResponse(rows.answers);
  const exams = toExams(rows.exams);
  const person = toPerson(rows.user);
  const { maxScoreByQuestion, validityDays }: QuestionnaireFacts = latest?.questionnaireId ? facts : {};
  const assessment = assessNamat({ response: latest?.response ?? null, exams, now, maxScoreByQuestion, validityDays, person });
  const lastExam = exams
    .filter((e) => e.status === 'completed')
    .sort((a, b) => b.date.getTime() - a.date.getTime())[0];
  const indicators = lastExam
    ? readIndicators(lastExam as ExamValues, {
        ageMonths: person.birthDate ? ageInMonths(person.birthDate, lastExam.date) : null,
        sex: person.sex,
      })
    : [];
  return { assessment, indicators };
}

/** summarize() after reading (cached) the facts of the questionnaire the rows need. */
async function summarizeRows(rows: ClinicalRows, now: Date): Promise<ClinicalSummary> {
  const questionnaireId = latestResponse(rows.answers)?.questionnaireId;
  const facts = questionnaireId ? await questionnaireFacts(questionnaireId) : {};
  return summarize(rows, facts, now);
}

/** One answer of the user's latest questionnaire, with the texts to show it (#124). */
export interface MyScaleAnswer {
  questionId: string;
  score: number;
  category?: string;
  questionText?: Bilingual;
  optionText?: Bilingual;
}

/** What the beneficiary's own «مقياس نمط» window shows (#124). */
export interface MyNamatScale {
  assessment: NamatAssessment;
  /** The latest questionnaire's answers per pillar; empty when not done. */
  pillars: PillarScore[];
  answersByPillar: { pillar: Pillar; answers: MyScaleAnswer[] }[];
  /**
   * The response before the latest, when it was filled in the Riyadh month
   * just before it: its scale as it stood then (exams up to its date).
   */
  previousMonth: { percent: number; filledAt: Date } | null;
  /** The exams behind the scale: the exam it used, each outcome's points, the split; null without a scale. */
  breakdown: ScaleBreakdown | null;
}

/** Pure: the breakdown of the scale summarize() gave for these rows. */
export function myScaleBreakdown(
  rows: ClinicalRows,
  facts: QuestionnaireFacts,
  now: Date,
  assessment: NamatAssessment
): ScaleBreakdown | null {
  const { validityDays }: QuestionnaireFacts = latestResponse(rows.answers)?.questionnaireId ? facts : {};
  return buildScaleBreakdown({ assessment, exams: toExams(rows.exams), now, validityDays, person: toPerson(rows.user) });
}

/**
 * Pure: the latest response's answers with their pillar and texts (the saved
 * ones first, then the questionnaire's), and the pillar scores.
 */
export function myScaleDetails(
  answerRows: DocumentData[],
  facts: QuestionnaireFacts
): Pick<MyNamatScale, 'pillars' | 'answersByPillar'> {
  const top = latestRow(answerRows);
  if (!top) return { pillars: [], answersByPillar: [] };
  const byId = new Map((facts.questions || []).map((q) => [q.id, q]));
  const answers: MyScaleAnswer[] = ((top.r.answers || []) as DocumentData[])
    .filter((a) => a.questionId)
    .map((a) => {
      const q = byId.get(a.questionId);
      const option = q?.options?.find((o) => o.id && o.id === a.optionId);
      return {
        questionId: a.questionId as string,
        score: Number(a.score) || 0,
        category: (a.category as string | undefined) || q?.category,
        questionText: (a.questionText as Bilingual | undefined) || q?.question,
        optionText: (a.optionText as Bilingual | undefined) || option?.text,
      };
    });
  const lookup = { maxScoreByQuestion: facts.maxScoreByQuestion };
  return { pillars: pillarScores(answers, lookup), answersByPillar: groupByPillar(answers, lookup) };
}

/** The response before the latest one, if it is from the month before; null otherwise. */
function previousMonthRow(answerRows: DocumentData[]): { r: DocumentData; at: Date } | null {
  const [latest, before] = datedRows(answerRows);
  return latest && before && isPreviousMonth(before.at, latest.at) ? before : null;
}

/**
 * The signed-in beneficiary's scale, pillars, own answers and the exams behind
 * the scale (#124): the same
 * 3 reads as loadClinicalSummary, plus the questionnaire facts (cached).
 */
export async function loadMyNamatScale(userId: string, now: Date = new Date()): Promise<MyNamatScale> {
  const rows = await readClinicalRows(userId);
  const questionnaireId = latestResponse(rows.answers)?.questionnaireId;
  const facts = questionnaireId ? await questionnaireFacts(questionnaireId) : {};

  let previousMonth: MyNamatScale['previousMonth'] = null;
  const before = previousMonthRow(rows.answers);
  if (before) {
    const beforeFacts = before.r.questionnaireId ? await questionnaireFacts(before.r.questionnaireId) : {};
    const then = summarize({ ...rows, answers: [before.r] }, beforeFacts, before.at).assessment;
    if (then.scale) previousMonth = { percent: then.scale.percent, filledAt: before.at };
  }

  const { assessment } = summarize(rows, facts, now);
  return {
    assessment,
    ...myScaleDetails(rows.answers, facts),
    previousMonth,
    breakdown: myScaleBreakdown(rows, facts, now, assessment),
  };
}

/** Birth date and sex from the user's profile (children's BMI, #90). */
export async function loadPerson(userId: string): Promise<PersonInput> {
  try {
    return toPerson((await getDoc(doc(db, 'users', userId))).data() || {});
  } catch {
    return {};
  }
}

export async function loadNamatAssessment(userId: string, now: Date = new Date()): Promise<NamatAssessment> {
  return (await loadClinicalSummary(userId, now)).assessment;
}

/** The Namat scale and the latest exam's indicators for one beneficiary (#93). */
export async function loadClinicalSummary(userId: string, now: Date = new Date()): Promise<ClinicalSummary> {
  return summarizeRows(await readClinicalRows(userId), now);
}

/** One beneficiary's answers, exams and profile: 3 reads. */
async function readClinicalRows(userId: string): Promise<ClinicalRows> {
  const [answersSnap, examsSnap, user] = await Promise.all([
    getDocs(query(collection(db, 'questionersanswers'), where('userId', '==', userId))),
    getDocs(query(collection(db, 'clinicalExams'), where('customerId', '==', userId))),
    getDoc(doc(db, 'users', userId))
      .then((snap): DocumentData | null => snap.data() || {})
      .catch(() => null),
  ]);
  return { answers: answersSnap.docs.map((d) => d.data()), exams: examsSnap.docs.map((d) => d.data()), user };
}

/** Firestore's `in` filter takes at most 30 values. */
export const CLINICAL_IN_CHUNK = IN_CHUNK;
/** How many chunks are read at once. */
export const CLINICAL_CHUNK_CONCURRENCY = CHUNK_CONCURRENCY;

/**
 * loadClinicalSummary for a whole roster (#73): 3 `in` queries per 30 users,
 * at most CLINICAL_CHUNK_CONCURRENCY chunks in flight, every user summarized
 * exactly as loadClinicalSummary does. A chunk whose answers or exams read
 * fails is logged and its users are left out of the map (unknown), as
 * loadClinicalSummary would reject for them; a failed users read only drops
 * the birth date and sex, as in loadClinicalSummary.
 */
export async function loadClinicalSummaries(
  userIds: string[],
  now: Date = new Date()
): Promise<Map<string, ClinicalSummary>> {
  const chunks = chunkIds(userIds, CLINICAL_IN_CHUNK);
  const out = new Map<string, ClinicalSummary>();

  await forEachLimited(chunks, CLINICAL_CHUNK_CONCURRENCY, async (part) => {
    try {
      const [answersSnap, examsSnap, users] = await Promise.all([
        getDocs(query(collection(db, 'questionersanswers'), where('userId', 'in', part))),
        getDocs(query(collection(db, 'clinicalExams'), where('customerId', 'in', part))),
        getDocs(query(collection(db, 'users'), where(documentId(), 'in', part)))
          .then((snap) => new Map(snap.docs.map((d) => [d.id, d.data()])))
          .catch(() => null),
      ]);
      const answersBy = groupBy(answersSnap.docs.map((d) => d.data()), 'userId');
      const examsBy = groupBy(examsSnap.docs.map((d) => d.data()), 'customerId');
      const summaries = await Promise.all(
        part.map((uid) =>
          summarizeRows(
            {
              answers: answersBy.get(uid) ?? [],
              exams: examsBy.get(uid) ?? [],
              user: users === null ? null : users.get(uid) ?? {},
            },
            now
          )
        )
      );
      part.forEach((uid, i) => out.set(uid, summaries[i]));
    } catch (error) {
      console.error('Error loading the Namat scale for a roster chunk:', error);
    }
  });
  return out;
}

/** loadClinicalSummaries, the assessment only (manager table). */
export async function loadNamatAssessments(
  userIds: string[],
  now: Date = new Date()
): Promise<Map<string, NamatAssessment>> {
  const summaries = await loadClinicalSummaries(userIds, now);
  return new Map(Array.from(summaries, ([uid, s]) => [uid, s.assessment]));
}
